/**
 * Admin User Management Service
 * API: /api/admin/users
 */

import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type {
  AdminUserListParams,
  AdminUserListResponse,
  ApplicationUserListItemDto,
  ApplicationUserDto,
  CreateApplicationUserCommand,
  UpdateApplicationUserCommand,
  ChangeUserPasswordCommand,
  StartUserImpersonationRequest,
  StartUserImpersonationResponse,
  EndCurrentImpersonationResponse,
} from '@/types/api/admin';

export class AdminUserService {
  private static instance: AdminUserService;

  private constructor() {}

  public static getInstance(): AdminUserService {
    if (!AdminUserService.instance) {
      AdminUserService.instance = new AdminUserService();
    }
    return AdminUserService.instance;
  }

  /**
   * Get list of all users with pagination and filters
   */
  async getUsers(params?: AdminUserListParams): Promise<AdminUserListResponse> {
    return await client.get<AdminUserListResponse>(
      API_ENDPOINTS.ADMIN.USERS.LIST,
      { params }
    );
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<ApplicationUserDto> {
    return await client.get<ApplicationUserDto>(
      API_ENDPOINTS.ADMIN.USERS.BY_ID(userId)
    );
  }

  /**
   * Create new user
   */
  async createUser(data: CreateApplicationUserCommand): Promise<ApplicationUserListItemDto> {
    return await client.post<ApplicationUserListItemDto>(
      API_ENDPOINTS.ADMIN.USERS.CREATE,
      data
    );
  }

  /**
   * Update existing user
   */
  async updateUser(userId: string, data: UpdateApplicationUserCommand): Promise<ApplicationUserListItemDto> {
    return await client.put<ApplicationUserListItemDto>(
      API_ENDPOINTS.ADMIN.USERS.UPDATE(userId),
      data
    );
  }

  async updateActiveMembership(userId: string, tenantId: string): Promise<{ membershipId: string; tenantId: string; isOwner: boolean; foundrySourceVersion: number }> {
    return await client.put(
      API_ENDPOINTS.ADMIN.USERS.ACTIVE_MEMBERSHIP(userId),
      { tenantId }
    );
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    return await client.delete<void>(
      API_ENDPOINTS.ADMIN.USERS.DELETE(userId)
    );
  }

  /**
   * Change user password (admin)
   */
  async changeUserPassword(userId: string, data: ChangeUserPasswordCommand): Promise<void> {
    return await client.post<void>(
      API_ENDPOINTS.ADMIN.USERS.CHANGE_PASSWORD(userId),
      data
    );
  }

  async startImpersonation(userId: string, data: StartUserImpersonationRequest): Promise<StartUserImpersonationResponse> {
    return await client.post<StartUserImpersonationResponse>(
      API_ENDPOINTS.ADMIN.USERS.IMPERSONATE(userId),
      data
    );
  }

  async endCurrentImpersonation(): Promise<EndCurrentImpersonationResponse> {
    return await client.post<EndCurrentImpersonationResponse>(
      API_ENDPOINTS.ADMIN.IMPERSONATION.END_CURRENT
    );
  }
}

export const adminUserService = AdminUserService.getInstance();
export default adminUserService;
